import { useCallback, useEffect, useRef, useState } from 'react';

// Balanza por cable (USB o RS-232 con adaptador) leída desde el navegador con Web Serial (Chrome y Edge).
// Casi todas las balanzas de plataforma mandan una línea de texto por lectura, por ejemplo
// "ST,GS,+0001025.0kg" (estable) o "US,GS,+0001019.5kg" (moviéndose). Se toma el número de la línea y la
// lectura cuenta como estable si la balanza lo dice (ST) o si el peso no cambia durante un segundo.

export type ScaleState = 'unsupported' | 'disconnected' | 'connecting' | 'connected' | 'error';

export interface ScaleReading {
  kg: number;
  stable: boolean;
  at: number;
  raw: string;
}

const STABLE_MS = 1000;
const TOLERANCE_KG = 0.2;

export function parseScaleLine(line: string): { kg: number; flag: 'stable' | 'unstable' | null } | null {
  const clean = line.trim();
  if (!clean) return null;
  const m = clean.match(/([+-]?\s*\d+(?:[.,]\d+)?)\s*(kg|g|lb)?\s*$/i) || clean.match(/([+-]?\s*\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  let kg = Number(m[1].replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(kg)) return null;
  const unit = (m[2] || 'kg').toLowerCase();
  if (unit === 'g') kg /= 1000;
  if (unit === 'lb') kg *= 0.45359237;
  const flag = /^\s*ST\b|\bST,/i.test(clean) ? 'stable' : /^\s*(US|MO)\b|\b(US|MO),/i.test(clean) ? 'unstable' : null;
  return { kg: Math.round(kg * 100) / 100, flag };
}

export const serialSupported = () => typeof navigator !== 'undefined' && 'serial' in navigator;

const BAUD_KEY = 'factory.scale.baud';

export function useSerialScale() {
  const [state, setState] = useState<ScaleState>(serialSupported() ? 'disconnected' : 'unsupported');
  const [reading, setReading] = useState<ScaleReading | null>(null);
  const [error, setError] = useState<string | null>(null);
  const portRef = useRef<any>(null);
  const readerRef = useRef<any>(null);
  const keepReading = useRef(false);
  const since = useRef<{ kg: number; t: number } | null>(null);

  const disconnect = useCallback(async () => {
    keepReading.current = false;
    try { await readerRef.current?.cancel(); } catch { /* ya cerrado */ }
    try { await portRef.current?.close(); } catch { /* ya cerrado */ }
    readerRef.current = null;
    portRef.current = null;
    setState(serialSupported() ? 'disconnected' : 'unsupported');
  }, []);

  useEffect(() => () => { disconnect(); }, [disconnect]);

  const connect = useCallback(async (baudRate?: number) => {
    if (!serialSupported()) { setState('unsupported'); return; }
    setError(null);
    setState('connecting');
    const baud = baudRate ?? (Number(localStorage.getItem(BAUD_KEY)) || 9600);
    try {
      const port = await (navigator as any).serial.requestPort();
      await port.open({ baudRate: baud });
      try { localStorage.setItem(BAUD_KEY, String(baud)); } catch { /* sin storage */ }
      portRef.current = port;
      keepReading.current = true;
      setState('connected');
      const decoder = new TextDecoderStream();
      port.readable.pipeTo(decoder.writable).catch(() => undefined);
      const reader = decoder.readable.getReader();
      readerRef.current = reader;
      let buffer = '';
      while (keepReading.current) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += value;
        const lines = buffer.split(/\r\n|\r|\n/);
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const p = parseScaleLine(line);
          if (!p) continue;
          const now = Date.now();
          if (!since.current || Math.abs(since.current.kg - p.kg) > TOLERANCE_KG) since.current = { kg: p.kg, t: now };
          const steady = now - since.current.t >= STABLE_MS;
          const stable = p.flag === 'stable' || (p.flag !== 'unstable' && steady);
          setReading({ kg: p.kg, stable, at: now, raw: line.trim() });
        }
      }
    } catch (e: any) {
      if (e?.name === 'NotFoundError') { setState('disconnected'); return; }
      setError(e?.message || 'No se pudo leer la balanza');
      setState('error');
      await disconnect();
      setState('error');
    }
  }, [disconnect]);

  return { state, reading, error, connect, disconnect };
}
